import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import {
  ApartmentOutlined,
  DirectionsCarOutlined,
  AccountBalanceOutlined,
  SecurityOutlined,
  DashboardOutlined,
  SettingsOutlined,
  SupportAgentOutlined,
  WidgetsOutlined,
  AssessmentOutlined,
} from "@mui/icons-material";
import { permissionCounts } from "./permissionUtils";

const moduleIcons = {
  came: DirectionsCarOutlined,
  copropiedades: ApartmentOutlined,
  finops: AccountBalanceOutlined,
  financiera: AccountBalanceOutlined,
  seguridad: SecurityOutlined,
  tableros: DashboardOutlined,
  mantenimiento: SettingsOutlined,
  noc: SupportAgentOutlined,
  indicadores: AssessmentOutlined,
};
export default function PermissionNavigation({ modules, activeKey, onSelect, permissions }) {
  const selectedModule = useRef(null);
  useEffect(() => {
    selectedModule.current?.scrollIntoView?.({ block: "nearest" });
  }, [activeKey]);
  return (
    <>
      <FormControl
        size="small"
        fullWidth
        sx={{ display: { xs: "flex", md: "none" }, mb: 2, flex: "0 0 auto" }}
      >
        <InputLabel id="role-module-label">Módulo</InputLabel>
        <Select
          labelId="role-module-label"
          label="Módulo"
          sx={{
            height: 44,
            "& .MuiSelect-select": { display: "flex", alignItems: "center", minHeight: "unset" },
            "& .MuiSelect-icon": { display: "block" },
          }}
          value={activeKey || ""}
          onChange={(event) => onSelect(event.target.value)}
          disabled={!modules.length}
        >
          {modules.map((node) => {
            const count = permissionCounts(node, permissions);
            return (
              <MenuItem key={node.key} value={node.key}>
                {node.name} ({count.selected}/{count.total})
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      <Box
        component="nav"
        aria-label="Módulos de permisos"
        sx={{
          display: { xs: "none", md: "block" },
          width: 260,
          bgcolor: "background.default",
          flexShrink: 0,
          overflowY: "auto",
          borderRight: "1px solid",
          borderColor: "divider",
          p: 1.5,
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "#cbd5e1", borderRadius: 4 },
        }}
      >
        <Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>
          Módulos
        </Typography>
        <List disablePadding>
          {modules.map((node) => {
            const count = permissionCounts(node, permissions);
            const ModuleIcon = moduleIcons[node.key] || WidgetsOutlined;
            return (
              <ListItemButton
                key={node.key}
                ref={activeKey === node.key ? selectedModule : null}
                selected={activeKey === node.key}
                onClick={() => onSelect(node.key)}
                aria-current={activeKey === node.key ? "true" : undefined}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  gap: 1,
                  py: 1.5,
                  border: "1px solid transparent",
                  "&.Mui-selected": {
                    bgcolor: "rgba(26,115,232,0.09)",
                    color: "info.main",
                    borderColor: "rgba(26,115,232,0.22)",
                  },
                  "&.Mui-selected:hover": { bgcolor: "rgba(26,115,232,0.12)" },
                }}
              >
                <ModuleIcon
                  sx={{
                    fontSize: 20,
                    color: activeKey === node.key ? "info.main" : "text.secondary",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ flex: 1, fontWeight: activeKey === node.key ? 700 : 500 }}
                >
                  {node.name}
                </Typography>
                <Chip
                  size="small"
                  label={`${count.selected}/${count.total}`}
                  variant="outlined"
                  sx={{
                    height: 24,
                    minWidth: 42,
                    borderRadius: 1.5,
                    fontSize: "0.68rem",
                    fontWeight: 600,
                    bgcolor: activeKey === node.key ? "rgba(26,115,232,0.1)" : "background.paper",
                    color: activeKey === node.key ? "info.main" : "text.secondary",
                    borderColor: "transparent",
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
        {!modules.length && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            No hay módulos que coincidan.
          </Typography>
        )}
      </Box>
    </>
  );
}
PermissionNavigation.propTypes = {
  modules: PropTypes.array.isRequired,
  activeKey: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  permissions: PropTypes.object.isRequired,
};
